import React from 'react';
import { motion } from 'framer-motion';
import { Scale, Zap } from 'lucide-react';

export default function Terms() {
  return (
    <div className="min-h-screen bg-black pt-32 pb-20">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600/10 border border-red-600/20 mb-6">
            <Scale size={14} className="text-red-600" />
            <span className="text-[10px] font-black tracking-[0.3em] uppercase text-red-600">Terms & Conditions</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-black italic uppercase leading-[0.85] text-white tracking-tighter mb-6">
            TERMS & <span className="text-gold">CONDITIONS</span>
          </h1>

          <p className="text-zinc-400 text-lg">Last updated: May 12, 2026</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8 text-zinc-300"
        >
          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">1. Acceptance of Terms</h2>
            <p className="leading-relaxed">
              By accessing and using Chaitanya Shooting Academy's website and services, you accept and agree to be bound by and abide by the terms and conditions outlined in this agreement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">2. Training Eligibility</h2>
            <ul className="space-y-3 list-disc list-inside">
              <li>You must be at least 7 years old to join training programs</li>
              <li>For minors, parental/guardian consent is mandatory</li>
              <li>You must be in good physical and mental health</li>
              <li>You must disclose any medical conditions affecting your ability to train</li>
              <li>We reserve the right to refuse service if you pose safety risks</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">3. Safety Protocols</h2>
            <p className="leading-relaxed mb-4">
              You acknowledge that shooting sports involve inherent risks. You agree to:
            </p>
            <ul className="space-y-2 list-disc list-inside">
              <li>Follow all safety instructions provided by coaches</li>
              <li>Wear all required protective equipment</li>
              <li>Comply with NRAI safety standards and range rules</li>
              <li>Not use our facilities while under influence of alcohol/drugs</li>
              <li>Report any hazardous conditions immediately</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">4. Liability Waiver</h2>
            <p className="leading-relaxed">
              You assume all risks associated with shooting sports training. Chaitanya Shooting Academy, its owners, coaches, and staff are not liable for injuries, damages, or losses resulting from:
            </p>
            <ul className="space-y-2 list-disc list-inside">
              <li>Your participation in training programs</li>
              <li>Use of academy facilities and equipment</li>
              <li>Accidents or incidents despite safety protocols</li>
              <li>Pre-existing medical conditions</li>
            </ul>
            <p className="mt-4 text-yellow-600 font-bold">By using our services, you waive your right to claim damages from the academy.</p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">5. Fees & Payment</h2>
            <ul className="space-y-3 list-disc list-inside">
              <li>Fees are due in advance of each training period</li>
              <li>We accept credit/debit cards and digital payments</li>
              <li>GST (18%) is applicable on all fees</li>
              <li>Late payments may result in suspension of training access</li>
              <li>All sales are final except as per our refund policy</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">6. Cancellation & Refund</h2>
            <ul className="space-y-2 list-disc list-inside">
              <li>Cancellations must be made 2 weeks before billing date</li>
              <li>Refunds are issued minus ₹500 administrative fee</li>
              <li>No refunds for completed training sessions</li>
              <li>Prorated refunds for annual plans if cancelled mid-term</li>
              <li>Refunds are processed within 7-10 business days</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">7. Attendance & Makeup Sessions</h2>
            <ul className="space-y-2 list-disc list-inside">
              <li>Missed sessions without notice (24 hrs) cannot be rescheduled</li>
              <li>One makeup session per month is allowed with 48 hrs notice</li>
              <li>Unused sessions expire at the end of each billing cycle</li>
              <li>Extended absences (30+ days) may result in membership termination</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">8. Code of Conduct</h2>
            <p className="leading-relaxed mb-4">You agree to maintain professional and respectful behavior. Prohibited conduct includes:</p>
            <ul className="space-y-2 list-disc list-inside">
              <li>Harassment or disrespect towards coaches/staff/students</li>
              <li>Misuse of academy facilities or equipment</li>
              <li>Photography/recording without permission</li>
              <li>Violation of safety protocols</li>
              <li>Any illegal activity on premises</li>
            </ul>
            <p className="mt-4"><strong className="text-white">Violation results in immediate termination of membership without refund.</strong></p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">9. Intellectual Property</h2>
            <p className="leading-relaxed">
              All content, coaching methods, and materials on our website and at our facility are the intellectual property of Chaitanya Shooting Academy. Unauthorized reproduction, modification, or distribution is prohibited.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">10. Photo/Video Release</h2>
            <p className="leading-relaxed">
              By training at our academy, you consent to photography and video recording for progress documentation and promotional purposes. You may opt-out by submitting written request to info@chaitanyashooting.com
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">11. Limitation of Liability</h2>
            <p className="leading-relaxed">
              To the maximum extent permitted by law, Chaitanya Shooting Academy's total liability for any claim shall not exceed the amount you paid for services in the past 12 months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">12. Dispute Resolution</h2>
            <p className="leading-relaxed mb-4">
              Any disputes shall be resolved through:
            </p>
            <ul className="space-y-2 list-disc list-inside">
              <li>Good faith negotiation within 30 days</li>
              <li>Mediation if negotiation fails</li>
              <li>Arbitration as per Indian Arbitration Act, 1996</li>
              <li>Jurisdiction: Gurugram, Haryana</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">13. Changes to Terms</h2>
            <p className="leading-relaxed">
              We may update these terms at any time. Continued use of our services constitutes acceptance of updated terms. We recommend reviewing this page periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">14. Contact</h2>
            <div className="p-6 bg-zinc-900/50 border border-white/10 rounded-2xl">
              <p className="mb-2"><strong className="text-white">Email:</strong> legal@chaitanyashooting.com</p>
              <p className="mb-2"><strong className="text-white">Phone:</strong> +91 96535 24055</p>
              <p><strong className="text-white">Address:</strong> Sector 81, Gurugram, Haryana 122050</p>
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
}
